import { Text } from "components/core/Text";
import { GoogleLoginButton } from "components/core/buttons/GoogleLoginButton";
import { IconButton } from "components/core/buttons/IconButton";
import { KakaoLoginButton } from "components/core/buttons/KakaoLoginButton";
import { motion } from "framer-motion";
import isWebview from "is-ua-webview";
import { XIcon } from "lucide-react";
import { ComponentProps, useMemo } from "react";

import { Button } from "../buttons/Button";
import { MixModal } from "./MixModal";

type SignInModalProps = ComponentProps<typeof MixModal> & {
    close: () => Promise<void>;
    // 사용자가 직접 닫을 때(X 버튼) 호출. 없으면 close 사용
    dismiss?: () => Promise<void>;
};

export const SignInModal = ({ close, dismiss, ...props }: SignInModalProps) => {
    return (
        <MixModal {...props}>
            <Default close={close} dismiss={dismiss ?? close} />
        </MixModal>
    );
};

function Default({
    close,
    dismiss
}: Pick<SignInModalProps, "close" | "dismiss">) {
    const webview = useMemo(() => isWebview(navigator.userAgent), []);

    return (
        <motion.div
            className="flex w-[342px] flex-col rounded-2xl bg-white px-4 pb-12
                pt-5 dark:bg-[#171717] md:w-[600px] md:pb-12"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{
                duration: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94] // power2.out easing
            }}
        >
            <div className="mb-2 flex justify-end">
                <motion.button
                    onClick={dismiss}
                    className="p-2"
                    whileHover={{ rotate: 90 }}
                    transition={{
                        duration: 0.2,
                        ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                >
                    <XIcon className="size-6 text-black dark:text-white" />
                </motion.button>
            </div>
            <div className="flex flex-col items-center gap-10">
                <header className="flex flex-col gap-2 break-keep text-center">
                    <Text
                        className="block min-w-[311px] text-common-black-main
                            dark:text-common-white-main md:hidden"
                        variant="h2"
                    >
                        믹스에 오신것을 환영합니다!
                    </Text>
                    <Text
                        className="hidden text-common-black-main
                            dark:text-common-white-main md:block"
                        variant="h2"
                    >{`믹스에 오신것을\n환영합니다!`}</Text>
                    <Text
                        className="md:text-sm md:font-normal
                            md:leading-[139.9999976158142%]"
                        variant="body1"
                        color="text.disabled"
                    >
                        믹스가 엄선한 마케팅 콘텐츠를 경험해보세요.
                    </Text>
                </header>
                <div className="flex flex-col items-center gap-6">
                    <div className="flex w-[250px] flex-col gap-3 md:w-[300px]">
                        {!webview && (
                            <motion.div
                                whileHover={{ scale: 1.02, y: -2 }}
                                whileTap={{ scale: 0.98 }}
                                transition={{
                                    duration: 0.2,
                                    ease: [0.25, 0.46, 0.45, 0.94]
                                }}
                            >
                                <GoogleLoginButton onSuccess={close} />
                            </motion.div>
                        )}
                        <motion.div
                            className="w-full"
                            whileHover={{ scale: 1.02, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                            transition={{
                                duration: 0.2,
                                ease: [0.25, 0.46, 0.45, 0.94]
                            }}
                        >
                            <KakaoLoginButton />
                        </motion.div>
                    </div>
                    <Text
                        className="w-[250px] break-keep text-center
                            text-text-placeholder md:w-[350px] [&_a]:underline"
                        variant="caption2"
                        tag="p"
                    >
                        {"로그인은 "}
                        <a
                            href="https://support.mix.day/e8ceb0d6-cba8-496f-873f-19e657abe168"
                            target="_blank"
                            rel="noreferrer"
                        >
                            개인정보 보호정책
                        </a>
                        {" 및 "}
                        <a
                            href="https://support.mix.day/7892acb6-7072-477d-abc1-224fe83fb855"
                            target="_blank"
                            rel="noreferrer"
                        >
                            서비스 약관
                        </a>
                        에 동의하는 것을 의미하며 서비스 이용을 위해 이메일과
                        이름, 프로필 이미지를 수집합니다.
                    </Text>
                </div>
            </div>
        </motion.div>
    );
}
